import { useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  NumberInput,
  TextInput,
  Select,
  Radio,
  Alert,
  Text,
  Loader,
  SimpleGrid,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { runtimeConfigApi } from '../lib/api';
import { CpuAllocation, DeployTarget, RuntimeConfigFields } from '../types';
import { showSuccess, showError } from '../lib/notifications';

export type RuntimeConfigTarget =
  | { scope: 'defaults'; serviceId: string }
  | { scope: 'override'; serviceId: string; envId: string; envName: string };

interface RuntimeConfigModalProps {
  target: RuntimeConfigTarget | null;
  onClose: () => void;
}

const emptyValues: RuntimeConfigFields = {
  cpu: null,
  memory: null,
  port: null,
  concurrency: null,
  minInstances: null,
  maxInstances: null,
  vpcConnector: null,
  authType: null,
  region: null,
  cpuAllocation: null,
  deployTarget: null,
};

export function RuntimeConfigModal({ target, onClose }: RuntimeConfigModalProps) {
  const queryClient = useQueryClient();
  const opened = !!target;

  const form = useForm<RuntimeConfigFields>({ initialValues: emptyValues });

  const defaultsQuery = useQuery({
    queryKey: ['runtime-config-defaults', target?.serviceId],
    queryFn: () => runtimeConfigApi.getDefaults(target!.serviceId).then((r) => r.data),
    enabled: opened,
  });

  const overrideQuery = useQuery({
    queryKey: ['runtime-config-override', target?.serviceId, target && (target as any).envId],
    queryFn: () =>
      runtimeConfigApi
        .getOverride(target!.serviceId, (target as any).envId)
        .then((r) => r.data),
    enabled: opened && target?.scope === 'override',
  });

  const current = target?.scope === 'defaults' ? defaultsQuery.data : overrideQuery.data;
  const isLoading =
    target?.scope === 'defaults' ? defaultsQuery.isLoading : overrideQuery.isLoading || defaultsQuery.isLoading;

  useEffect(() => {
    if (opened) {
      form.setValues(current ? { ...emptyValues, ...current } : emptyValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, current]);

  const save = useMutation({
    mutationFn: async (values: RuntimeConfigFields): Promise<void> => {
      const t = target!;
      if (t.scope === 'defaults') {
        await runtimeConfigApi.updateDefaults(t.serviceId, values);
      } else {
        await runtimeConfigApi.updateOverride(t.serviceId, t.envId, values);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['runtime-config-defaults', target?.serviceId]);
      queryClient.invalidateQueries(['runtime-config-override', target?.serviceId]);
      showSuccess('Runtime settings saved. They take effect on the next deploy.');
      onClose();
    },
    onError: (error: any) => showError(error, 'Failed to save runtime settings'),
  });

  const resetOverride = useMutation({
    mutationFn: async (): Promise<void> => {
      if (target?.scope !== 'override') return;
      await runtimeConfigApi.deleteOverride(target.serviceId, target.envId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['runtime-config-override', target?.serviceId]);
      showSuccess('Override cleared — this environment now inherits the service defaults.');
      onClose();
    },
    onError: (error: any) => showError(error, 'Failed to reset override'),
  });

  const inheritedMinInstances =
    target?.scope === 'override' ? defaultsQuery.data?.minInstances ?? null : null;
  const effectiveMinInstances = form.values.minInstances ?? inheritedMinInstances ?? 0;
  const instanceBasedInvalid =
    form.values.cpuAllocation === CpuAllocation.INSTANCE_BASED && effectiveMinInstances < 1;

  // Wisp is offered for non-production environments while it runs alongside Cloud Run.
  const canPickTarget = target?.scope === 'override' && target.envName !== 'production';
  // Unset means the backend default: both, while Wisp is validated against Cloud Run.
  const target_ = form.values.deployTarget ?? DeployTarget.BOTH;
  const usesWisp = canPickTarget && target_ !== DeployTarget.CLOUD_RUN;

  const placeholder = (field: keyof RuntimeConfigFields, fallback: string) =>
    target?.scope === 'override' && defaultsQuery.data?.[field] != null
      ? `Inherits: ${defaultsQuery.data[field]}`
      : fallback;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        target?.scope === 'defaults'
          ? 'Service Runtime Defaults'
          : `Runtime Override — ${target?.scope === 'override' ? target.envName : ''}`
      }
      size="lg"
    >
      {isLoading ? (
        <Group position="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack spacing="md">
          <Text size="sm" color="dimmed">
            Changes apply on the next deploy of this{' '}
            {target?.scope === 'defaults' ? 'service' : 'environment'} — not immediately.
          </Text>

          {canPickTarget && (
            <Select
              label="Deploy target"
              description="Where this environment is built and run on merge"
              data={[
                { value: DeployTarget.CLOUD_RUN, label: 'Cloud Run (Cloud Build)' },
                { value: DeployTarget.WISP, label: 'Wisp (Foundry + Runway)' },
                { value: DeployTarget.BOTH, label: 'Both — Cloud Run and Wisp (default)' },
              ]}
              value={target_}
              onChange={(value) => form.setFieldValue('deployTarget', (value || null) as DeployTarget | null)}
            />
          )}

          {usesWisp && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              On Wisp, CPU, memory, port, instances and concurrency apply (instances are capped at 10).
              Auth type, VPC connector, region and CPU allocation are Cloud Run settings and are ignored there.
            </Alert>
          )}

          <Radio.Group
            label="CPU allocation"
            value={form.values.cpuAllocation ?? ''}
            onChange={(value) =>
              form.setFieldValue('cpuAllocation', (value || null) as CpuAllocation | null)
            }
          >
            <Stack mt="xs" spacing="xs">
              <Radio
                value={CpuAllocation.REQUEST_BASED}
                label="Request-based (scale to zero, cheaper)"
              />
              <Radio
                value={CpuAllocation.INSTANCE_BASED}
                label="Instance-based (always allocated — background work/websockets)"
              />
            </Stack>
          </Radio.Group>

          {form.values.cpuAllocation === CpuAllocation.INSTANCE_BASED && (
            <Alert icon={<IconAlertTriangle size={16} />} color="yellow">
              Instance-based allocation keeps CPU reserved even while idle — expect higher
              cost than request-based. Requires Min Instances of at least 1.
            </Alert>
          )}

          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            <NumberInput
              label="CPU (vCPUs)"
              placeholder={placeholder('cpu', 'Cloud Run default (1)')}
              min={1}
              value={form.values.cpu ?? undefined}
              onChange={(value) => form.setFieldValue('cpu', value === '' ? null : value)}
            />
            <TextInput
              label="Memory"
              placeholder={placeholder('memory', 'e.g. 512Mi, 2Gi')}
              value={form.values.memory ?? ''}
              onChange={(e) => form.setFieldValue('memory', e.currentTarget.value || null)}
            />
          </SimpleGrid>

          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            <NumberInput
              label="Min instances"
              placeholder={placeholder('minInstances', 'Cloud Run default (0)')}
              min={0}
              error={instanceBasedInvalid ? 'Required (>= 1) for instance-based allocation' : undefined}
              value={form.values.minInstances ?? undefined}
              onChange={(value) =>
                form.setFieldValue('minInstances', value === '' ? null : value)
              }
            />
            <NumberInput
              label="Max instances"
              placeholder={placeholder('maxInstances', 'Cloud Run default (100)')}
              min={1}
              value={form.values.maxInstances ?? undefined}
              onChange={(value) =>
                form.setFieldValue('maxInstances', value === '' ? null : value)
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            <NumberInput
              label="Concurrency"
              placeholder={placeholder('concurrency', 'Cloud Run default (80)')}
              min={1}
              value={form.values.concurrency ?? undefined}
              onChange={(value) =>
                form.setFieldValue('concurrency', value === '' ? null : value)
              }
            />
            <Select
              label="Auth type"
              placeholder={placeholder('authType', 'unauthenticated')}
              data={[
                { value: 'unauthenticated', label: 'Unauthenticated (public)' },
                { value: 'iam', label: 'IAM only' },
              ]}
              clearable
              value={form.values.authType ?? null}
              onChange={(value) => form.setFieldValue('authType', value as any)}
            />
          </SimpleGrid>

          <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            <TextInput
              label="VPC connector"
              placeholder={placeholder('vpcConnector', 'None')}
              value={form.values.vpcConnector ?? ''}
              onChange={(e) =>
                form.setFieldValue('vpcConnector', e.currentTarget.value || null)
              }
            />
            <TextInput
              label="Region"
              placeholder={placeholder('region', 'asia-southeast1')}
              value={form.values.region ?? ''}
              onChange={(e) => form.setFieldValue('region', e.currentTarget.value || null)}
            />
          </SimpleGrid>

          <Group position="apart" mt="md">
            {target?.scope === 'override' ? (
              <Button
                variant="subtle"
                color="red"
                onClick={() => resetOverride.mutate()}
                loading={resetOverride.isLoading}
              >
                Reset to defaults
              </Button>
            ) : (
              <span />
            )}
            <Group position="right">
              <Button variant="subtle" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate(form.values)}
                loading={save.isLoading}
                disabled={instanceBasedInvalid}
              >
                Save
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
